import React, { useState, useEffect, useCallback, useRef } from "react";
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent, { TileStatusContent } from '../../../components/Tile/TileContent';
import RefreshButton from '../../../components/Tile/RefreshButton';
import { useGlobalAPI } from "../../../hooks/useGlobalAPI";
import { useEdgeDuration, useEdgeTimeAvailableActivities } from "./hooks/useEdgeDuration";
import { getFileMetadata } from "../../visualization/LeadTimeHistogram/api/leadTimeHistogramApi";
import { useGraphData } from "../../../stores/GraphDataContext";
import { useAutoFitFontSize } from "../_shared/useAutoFitFontSize";

const EDGE_DURATION_VALUE_MAX_PX = 30; // was text-3xl
const EDGE_DURATION_VALUE_MIN_PX = 16;

// Extracted so useAutoFitFontSize is called from its own component (mounted only while data is
// present) rather than conditionally within EdgeDuration's own render — see Rules of Hooks.
// No compact fallback: this is a formatted duration string ("3.2h"), not a raw large count.
const EdgeDurationBigValue = ({ text }) => {
  const { ref, fontSizePx } = useAutoFitFontSize({
    text,
    maxPx: EDGE_DURATION_VALUE_MAX_PX,
    minPx: EDGE_DURATION_VALUE_MIN_PX,
  });

  return (
    <div
      ref={ref}
      className="max-w-full truncate font-bold text-gray-900"
      style={{ fontSize: `${fontSizePx}px` }}
    >
      {text}
    </div>
  );
};

const EdgeDuration = ({
  activeFile,
  onRefresh,
  onGetHeaderActions
}) => {
  const { filteredGraphData: graphData } = useGraphData();
  const api = useGlobalAPI(activeFile);
  const { data, loading, error, hasFetched, fetchKPIData, reset } = useEdgeDuration(activeFile);
  const { activities: activityTypes, loading: activityTypesLoading } = useEdgeTimeAvailableActivities(activeFile);

  const [objectType, setObjectType] = useState('');
  const [fromActivity, setFromActivity] = useState('');
  const [toActivity, setToActivity] = useState('');
  const [customTitle, setCustomTitle] = useState('Edge Time KPI');
  const [isSettingsCollapsed, setIsSettingsCollapsed] = useState(() => {
    const saved = localStorage.getItem("edgeTimeKPI_settingsCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [fileMetadata, setFileMetadata] = useState(null);
  const [objectTypes, setObjectTypes] = useState([]);

  useEffect(() => {
    localStorage.setItem(
      "edgeTimeKPI_settingsCollapsed",
      JSON.stringify(isSettingsCollapsed)
    );
  }, [isSettingsCollapsed]);

  useEffect(() => {
    reset();
    setObjectType('');
    setFromActivity('');
    setToActivity('');
    setCustomTitle('Edge Time KPI');
    setFileMetadata(null);
    setObjectTypes([]);
  }, [activeFile, reset]);

  useEffect(() => {
    const fetchMetadata = async () => {
      if (!activeFile) {
        setObjectTypes([]);
        return;
      }

      try {
        console.log('Fetching metadata for EdgeDuration:', activeFile);
        const metadata = await getFileMetadata(activeFile);
        setFileMetadata(metadata);

        if (metadata?.object_types) {
          const types = Object.keys(metadata.object_types).sort();
          setObjectTypes(types);
          console.log('Object types loaded for EdgeDuration:', types);
        } else {
          setObjectTypes(['Auftrag', 'Angebot', 'Lieferschein'].sort());
        }
      } catch (error) {
        console.error('Error fetching metadata for EdgeDuration:', error);
        setObjectTypes(['Auftrag', 'Angebot', 'Lieferschein'].sort());
      }
    };

    fetchMetadata();
  }, [activeFile]);

  // Auto-select first available object type
  useEffect(() => {
    if (objectTypes.length > 0 && !objectType) {
      setObjectType(objectTypes[0]);
    }
  }, [objectTypes, objectType]);

  // Extract activities from graph data filtered by object type
  const graphActivities = React.useMemo(() => {
    console.log('EdgeDuration - Graph data:', graphData);
    console.log('EdgeDuration - Graph data keys:', graphData ? Object.keys(graphData) : 'null');
    console.log('EdgeDuration - Selected object type:', objectType);

    if (!graphData?.edges_performance?.event_couples || !objectType) {
      console.log('EdgeDuration - No edges_performance data or object type not selected');
      return [];
    }

    // Get activities that exist for the selected object type
    const objectEdges = graphData.edges_performance.event_couples[objectType];
    console.log('EdgeDuration - Object edges for', objectType, ':', objectEdges);

    if (!objectEdges) {
      console.log('EdgeDuration - No edges found for object type:', objectType);
      return [];
    }

    // Extract all unique activities from the edge keys
    const activitySet = new Set();
    Object.keys(objectEdges).forEach(edgeKey => {
      const [fromActivity, toActivity] = edgeKey.split(',');
      if (fromActivity) activitySet.add(fromActivity);
      if (toActivity) activitySet.add(toActivity);
    });

    const activities = Array.from(activitySet).sort();
    console.log('EdgeDuration - Found activities for', objectType, ':', activities.length, activities);
    return activities;
  }, [graphData, objectType]);

  // Auto-select first available activities if none selected
  useEffect(() => {
    if (graphActivities.length > 0) {
      if (!fromActivity && graphActivities.length >= 1) {
        setFromActivity(graphActivities.find(a => a.includes('Auftrag anlegen')) || graphActivities[0]);
      }
      if (!toActivity && graphActivities.length >= 2) {
        setToActivity(graphActivities.find(a => a.includes('Lieferschein')) || graphActivities[1]);
      }
    }
  }, [graphActivities, fromActivity, toActivity]);

  const toggleSettings = () => {
    setIsSettingsCollapsed((prev) => !prev);
  };

  // Extract edge time data directly from graph data
  const extractEdgeTimeData = useCallback((fromActivity, toActivity) => {
    console.log('🔍 Extracting edge time data for:', { fromActivity, toActivity, objectType });
    console.log('🔍 Graph data structure:', {
      hasGraphData: !!graphData,
      hasEdgesPerformance: !!graphData?.edges_performance,
      hasEventCouples: !!graphData?.edges_performance?.event_couples,
      selectedObjectType: objectType
    });

    if (!graphData?.edges_performance?.event_couples || !objectType || !fromActivity || !toActivity) {
      console.log('Missing required data: object type, activities, or graph data');
      return null;
    }

    // Get the specific object type data
    const objectEdges = graphData.edges_performance.event_couples[objectType];
    if (!objectEdges) {
      console.log(`No edges found for object type: ${objectType}`);
      return null;
    }

    // Find the edge data for the specific activity pair
    const activityPairKey = `${fromActivity},${toActivity}`;
    console.log(`Looking for key: ${activityPairKey} in object type: ${objectType}`);

    const edgeData = objectEdges[activityPairKey];
    if (!edgeData) {
      console.log(`No edge data found for key: ${activityPairKey} in object type: ${objectType}`);
      return null;
    }

    console.log(`Found data for key ${activityPairKey}:`, edgeData);

    if (!Array.isArray(edgeData) || edgeData.length === 0) {
      console.log(`Invalid edge data for key ${activityPairKey}:`, edgeData);
      return null;
    }

    // Validate that durations are numbers
    const validDurations = edgeData.filter(d => typeof d === 'number' && !isNaN(d));
    console.log(`Valid durations: ${validDurations.length}/${edgeData.length}`);

    if (validDurations.length === 0) {
      console.log(`No valid numeric durations found for key ${activityPairKey}`);
      return null;
    }

    try {
      // Calculate statistics
      const sorted = [...validDurations].sort((a, b) => a - b);
      const avg = validDurations.reduce((a, b) => a + b, 0) / validDurations.length;
      const median = sorted[Math.floor(sorted.length / 2)];
      const min = sorted[0];
      const max = sorted[sorted.length - 1];
      const totalEdges = validDurations.length;

      console.log('Calculated statistics:', { avg, median, min, max, totalEdges });

      return {
        average_time_seconds: avg,
        median_time_seconds: median,
        min_time_seconds: min,
        max_time_seconds: max,
        total_edges: totalEdges
      };
    } catch (error) {
      console.error('Error calculating statistics:', error);
      throw new Error(`Failed to calculate statistics: ${error.message}`);
    }
  }, [graphData]);

  const handleUpdate = useCallback(async () => {
    console.log('handleUpdate called with:', {
      fromActivity,
      toActivity,
      customTitle
    });

    if (!fromActivity || !toActivity) {
      console.warn('Prevented KPI request with missing activities');
      return;
    }

    if (fromActivity === toActivity) {
      console.warn('Prevented KPI request with same from/to activities');
      return;
    }

    try {
      // Extract data directly from graph data instead of API call
      const edgeData = extractEdgeTimeData(fromActivity, toActivity);

      if (edgeData) {
        // Simulate the API response structure
        const mockResponse = edgeData;
        console.log("Edge Time data extracted:", mockResponse);
        // Since we're not using the hook anymore for data fetching, we'll set the data directly
        // This is a temporary solution - ideally we'd refactor to not use the hook
        setLocalData(mockResponse);
        setLocalLoading(false);
        setLocalError(null);
        setLocalHasFetched(true);
      } else {
        setLocalError("No edge data found for the selected activities");
        setLocalData(null);
        setLocalLoading(false);
        setLocalHasFetched(true);
      }
    } catch (error) {
      console.error('Error extracting edge time data:', error);
      setLocalError(`Error calculating KPI: ${error.message}`);
      setLocalData(null);
      setLocalLoading(false);
      setLocalHasFetched(true);
    }
  }, [extractEdgeTimeData, fromActivity, toActivity, customTitle]);

  // Local state to replace the hook's state since we're not using API calls
  const [localData, setLocalData] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [localHasFetched, setLocalHasFetched] = useState(false);

  const handleReset = useCallback(() => {
    console.log('Resetting EdgeDuration state');
    setFromActivity('');
    setToActivity('');
    setCustomTitle('Edge Time KPI');
    setLocalData(null);
    setLocalError(null);
    setLocalHasFetched(false);
    setLocalLoading(false);
  }, []);

  // Auto-refetch when global filters change if a result has already been calculated
  useEffect(() => {
    if (localHasFetched && !localLoading && fromActivity && toActivity) {
      handleUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.currentFilters]);

  useEffect(() => {
    if (onRefresh) {
      onRefresh(handleUpdate);
    }
  }, [onRefresh, handleUpdate]);

  useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(() => (
        <div className="flex items-center gap-2">
          <RefreshButton onClick={handleUpdate} loading={localLoading} />
          <button
            onClick={toggleSettings}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-200"
            title={isSettingsCollapsed ? "Show Settings" : "Hide Settings"}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      ));
    }
  }, [onGetHeaderActions, handleUpdate, localLoading, toggleSettings, isSettingsCollapsed]);

  const showInitialButton = !localHasFetched && isSettingsCollapsed;
  const shouldShowError = localError && localHasFetched;

  const formatTimeValue = (seconds) => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    } else if (seconds < 3600) {
      return `${(seconds / 60).toFixed(1)}m`;
    } else if (seconds < 86400) {
      return `${(seconds / 3600).toFixed(1)}h`;
    } else {
      return `${(seconds / 86400).toFixed(1)}d`;
    }
  };

  return (
    <BaseTile
      loading={localLoading}
      error={null} // Don't show error at tile level - show in settings instead
      minHeight="h-72"
      padding="sm"
      className=""
    >
      <TileContent layout="flex" direction="column" grow>
        {/* Initial Load Button */}
        {showInitialButton && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={() => setIsSettingsCollapsed(false)}
              disabled={localLoading}
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Configure KPI
            </button>
          </div>
        )}

        {/* Control Bar - Always Visible */}
        {!showInitialButton && (
          <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-200">
            <button
              onClick={handleUpdate}
              disabled={localLoading || !objectType || !fromActivity || !toActivity || !customTitle.trim()}
              className={`px-3 py-1 text-white text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 ${
                objectType && fromActivity && toActivity && !localLoading && customTitle.trim()
                ? "bg-primary-500 hover:bg-primary-600 focus:ring-primary-500"
                : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              {localLoading ? "Calculating..." : "Calculate KPI"}
            </button>
            <button
              onClick={handleReset}
              disabled={localLoading || !localHasFetched}
              className={`px-3 py-1 text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 ${
                localHasFetched && !localLoading
                ? "bg-gray-500 text-white hover:bg-gray-600 focus:ring-gray-500"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Reset
            </button>
            <button
              onClick={toggleSettings}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-expanded={!isSettingsCollapsed}
              aria-label={isSettingsCollapsed ? "Show settings" : "Hide settings"}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span>{isSettingsCollapsed ? "Settings" : "Hide"}</span>
            </button>
          </div>
        )}

        {/* Settings Panel */}
        {!isSettingsCollapsed && !showInitialButton && (
          <div className="overflow-hidden transition-all duration-200 ease-in-out mb-4">
            <div className="space-y-4 pb-4 border-b border-gray-200">
              {/* KPI Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  KPI Title *
                </label>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Order to Delivery Time"
                  disabled={localLoading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Custom name for your KPI widget
                </p>
              </div>

              {/* Object Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Object Type *
                </label>
                <select
                  value={objectType}
                  onChange={(e) => {
                    setObjectType(e.target.value);
                    // Reset activities when object type changes
                    setFromActivity('');
                    setToActivity('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={localLoading || objectTypes.length === 0}
                >
                  <option value="">
                    {objectTypes.length === 0 ? "Loading..." : "Select Object Type..."}
                  </option>
                  {objectTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Object type for edge analysis
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                {/* From Activity Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Activity *
                  </label>
                  <select
                    value={fromActivity}
                    onChange={(e) => setFromActivity(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    disabled={localLoading || graphActivities.length === 0}
                  >
                    <option value="">
                      {graphActivities.length === 0 ? "Generate graph first..." : "Select From Activity..."}
                    </option>
                    {graphActivities.map((activity) => (
                      <option key={activity} value={activity}>
                        {activity}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Starting activity for time measurement
                  </p>
                </div>

                {/* To Activity Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Activity *
                  </label>
                  <select
                    value={toActivity}
                    onChange={(e) => setToActivity(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    disabled={localLoading || graphActivities.length === 0}
                  >
                    <option value="">
                      {graphActivities.length === 0 ? "Generate graph first..." : "Select To Activity..."}
                    </option>
                    {graphActivities.filter(activity => activity !== fromActivity).map((activity) => (
                      <option key={activity} value={activity}>
                        {activity}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Ending activity for time measurement
                  </p>
                </div>
              </div>

              {/* Graph Generation Notice */}
              {graphActivities.length === 0 && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-800 mb-1">
                        Graph Data Required
                      </h4>
                      <p className="text-sm text-blue-700">
                        To use this KPI, you need to generate the process graph first. Go to the "Process Graph" section at the bottom of the page and click "Generate Graph".
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Messages */}
              {shouldShowError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-red-800 mb-1">
                        Calculation Error
                      </h4>
                      <p className="text-sm text-red-700">
                        {localError.includes('No edge data found') ?
                          "Invalid Selection: The selected edge does not exist in the current data set. Please choose an available edge and try again." :
                         localError}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Validation Messages */}
              {fromActivity && toActivity && fromActivity === toActivity && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-amber-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-amber-700">
                      From and To activities must be different
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* KPI Results */}
        {localData && !localLoading && (
          <div className="space-y-4">
            {/* Main KPI Value */}
            <div className="text-center">
              <EdgeDurationBigValue text={formatTimeValue(localData.average_time_seconds)} />
              <div className="text-sm text-gray-500">
                Average Time: {fromActivity} → {toActivity}
              </div>
            </div>

            {/* Breakdown Details */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-green-50 p-2.5 rounded-lg">
                <div className="text-lg font-semibold text-green-900">
                  {localData.min_time_seconds ? formatTimeValue(localData.min_time_seconds) : '—'}
                </div>
                <div className="text-xs text-green-700">
                  Min Time
                </div>
              </div>
              <div className="bg-red-50 p-2.5 rounded-lg">
                <div className="text-lg font-semibold text-red-900">
                  {localData.max_time_seconds ? formatTimeValue(localData.max_time_seconds) : '—'}
                </div>
                <div className="text-xs text-red-700">
                  Max Time
                </div>
              </div>
              <div className="bg-blue-50 p-2.5 rounded-lg">
                <div className="text-lg font-semibold text-blue-900">
                  {localData.total_edges?.toLocaleString('de-DE') || '0'}
                </div>
                <div className="text-xs text-blue-700">
                  Count
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State with Instructional Placeholders */}
        {!localData && !localLoading && !showInitialButton && (
          <TileStatusContent
            status="empty"
            title="No Edge Time Data"
            message="Select From and To activities in the settings panel, then calculate the KPI."
            icon={
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        )}
      </TileContent>
    </BaseTile>
  );
};

export default EdgeDuration;
